import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate, fmtNum } from "@/lib/helpers";
import { User, Package, Zap, ClipboardList, FileImage, Loader2, CheckCircle2, AlertCircle, HelpCircle, Pencil, ShieldCheck, Save, X, History, ArrowRight, Download } from "lucide-react";
import { toast } from "sonner";
import { canAct } from "@/lib/access";

export function AuthedImage({ path, downloadName }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  const [blob, setBlob] = useState(null);
  useEffect(() => {
    if (!path) return;
    let revoke = null;
    (async () => {
      try {
        const res = await http.get(`/files/${encodeURI(path)}`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        setSrc(url);
        setBlob(res.data);
        revoke = url;
      } catch { setErr(true); }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [path]);

  const download = () => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const ext = (blob.type || "image/jpeg").split("/")[1] || "jpg";
    a.href = url; a.download = `${downloadName || "kimlik"}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (err) return <div className="aspect-[16/10] rounded-xl bg-slate-800/60 border border-red-500/30 flex items-center justify-center text-xs text-red-300">Yüklenemedi</div>;
  if (!src) return <div className="aspect-[16/10] rounded-xl bg-slate-800/60 border border-slate-700 flex items-center justify-center"><Loader2 className="animate-spin text-slate-500" size={18} /></div>;
  return (
    <div className="relative group">
      <img src={src} alt="Kimlik" className="w-full aspect-[16/10] object-cover rounded-xl border border-slate-700" />
      <button
        type="button"
        onClick={download}
        data-testid={`download-${downloadName}`}
        className="absolute top-2 right-2 h-8 w-8 rounded-lg bg-slate-900/80 border border-slate-700 flex items-center justify-center text-slate-200 hover:bg-blue-600 hover:border-blue-500 transition opacity-0 group-hover:opacity-100 focus:opacity-100"
        title="İndir"
      >
        <Download size={14} />
      </button>
    </div>
  );
}

const STATUS_META = {
  tamam:    { label: "Tamam",     cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", Icon: CheckCircle2 },
  beklemede:{ label: "Beklemede", cls: "bg-amber-500/20 text-amber-300 border-amber-500/40",       Icon: HelpCircle },
  eksik:    { label: "Eksik",     cls: "bg-slate-700 text-slate-300 border-slate-600",             Icon: AlertCircle },
  sorunlu:  { label: "Sorunlu",   cls: "bg-red-500/20 text-red-300 border-red-500/40",             Icon: AlertCircle },
};

export default function SaleDetailDialog({ saleId, onClose, onUpdated }) {
  const { user } = useAuth();
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [idReviewOpen, setIdReviewOpen] = useState(false);
  const [reviewStatus, setReviewStatus] = useState("tamam");
  const [reviewNote, setReviewNote] = useState("");
  const [tab, setTab] = useState("detail");
  const [history, setHistory] = useState(null);

  const load = () => {
    if (!saleId) return;
    setLoading(true);
    http.get(`/sales/${saleId}`).then((r) => { setSale(r.data); setDraft({ product_id: r.data.product_id, quantity: r.data.quantity, customer_note: r.data.customer_note || "" }); })
      .finally(() => setLoading(false));
  };
  const loadHistory = () => {
    if (!saleId) return;
    http.get(`/sales/${saleId}/history`).then((r) => setHistory(r.data)).catch(() => setHistory(null));
  };
  useEffect(() => {
    if (!saleId) { setSale(null); setHistory(null); setEditing(false); setTab("detail"); return; }
    load();
    loadHistory();
    if (products.length === 0) http.get("/products?active_only=true").then((r) => setProducts(r.data));
    // eslint-disable-next-line
  }, [saleId]);

  const canEdit = (() => {
    if (!sale || !user) return false;
    if (user.role === "koordinator") return true;
    const created = new Date(sale.created_at);
    const now = new Date();
    const diffHr = (now - created) / 3_600_000;
    if (user.role === "personel") return sale.user_id === user.id && diffHr <= 1;
    if (user.role === "mudur") {
      if (sale.branch_id !== user.branch_id) return false;
      // Same calendar day in Istanbul (UTC+3)
      const toTR = (d) => new Date(d.getTime() + (3 * 60 - -d.getTimezoneOffset()) * 60000);
      return toTR(created).toDateString() === toTR(now).toDateString();
    }
    return false;
  })();

  const canReviewId = user && canAct(user, "approve_id") && (!sale || user.role !== "mudur" || sale.branch_id === user.branch_id);

  const saveEdit = async () => {
    try {
      const payload = {
        product_id: draft.product_id,
        quantity: Number(draft.quantity),
        customer_note: draft.customer_note || null,
      };
      await http.put(`/sales/${sale.id}`, payload);
      toast.success("Satış güncellendi");
      setEditing(false);
      load();
      loadHistory();
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    }
  };

  const saveReview = async () => {
    try {
      await http.patch(`/sales/${sale.id}/id-status`, { status: reviewStatus, note: reviewNote || null });
      toast.success("Kimlik durumu güncellendi");
      setIdReviewOpen(false);
      setReviewNote("");
      load();
      loadHistory();
      onUpdated?.();
    } catch { toast.error("Güncellenemedi"); }
  };

  const openReview = () => {
    setReviewStatus(sale?.id_card_status === "eksik" || sale?.id_card_status === "beklemede" ? "tamam" : sale?.id_card_status || "tamam");
    setReviewNote(sale?.id_card_review_note || "");
    setIdReviewOpen(true);
  };

  const idStatus = sale?.id_card_status || (sale?.id_card_front_path && sale?.id_card_back_path ? "beklemede" : "eksik");
  const meta = STATUS_META[idStatus] || STATUS_META.eksik;
  const StatusIcon = meta.Icon;
  const selectedProduct = products.find((p) => p.id === draft.product_id);
  const estPoints = selectedProduct && draft.quantity ? selectedProduct.points * Number(draft.quantity) : 0;

  return (
    <>
    <Dialog open={!!saleId} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sale-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2"><ClipboardList size={18} className="text-blue-400" /> Satış Detayı</span>
            {sale && (
              <div className="flex gap-2">
                {canReviewId && (
                  <Button size="sm" variant="outline" onClick={openReview} className="border-slate-700 bg-slate-800/50 h-7 text-xs" data-testid="sale-review-id-btn">
                    <ShieldCheck size={12} className="mr-1" /> Kimlik İnceleme
                  </Button>
                )}
                {canEdit && !editing && (
                  <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="border-blue-500/40 bg-blue-500/10 text-blue-300 h-7 text-xs" data-testid="sale-edit-btn">
                    <Pencil size={12} className="mr-1" /> Düzenle
                  </Button>
                )}
              </div>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading && <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin" /></div>}

        {sale && !editing && (
          <>
            <div className="flex gap-1 border-b border-slate-800 -mt-2 mb-3">
              <button onClick={() => setTab("detail")} data-testid="tab-detail"
                className={`px-3 py-2 text-xs font-semibold border-b-2 transition ${tab === "detail" ? "border-blue-400 text-blue-300" : "border-transparent text-slate-500 hover:text-slate-300"}`}>
                <ClipboardList size={12} className="inline mr-1" /> Detay
              </button>
              <button onClick={() => setTab("history")} data-testid="tab-history"
                className={`px-3 py-2 text-xs font-semibold border-b-2 transition ${tab === "history" ? "border-blue-400 text-blue-300" : "border-transparent text-slate-500 hover:text-slate-300"}`}>
                <History size={12} className="inline mr-1" /> Geçmiş {history?.entries && <span className="ml-1 text-[10px] text-slate-500">({history.entries.length})</span>}
              </button>
            </div>
          </>
        )}

        {sale && !editing && tab === "detail" && (
          <div className="space-y-4">
            {/* Status badge */}
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={`inline-flex items-center gap-1 ${meta.cls}`} data-testid={`sale-id-status-${idStatus}`}>
                <StatusIcon size={12} /> Kimlik: {meta.label}
              </Badge>
              {sale.edited_at && (
                <Badge className="bg-slate-800 text-slate-400 border-slate-700 text-[10px]">
                  Düzenlendi: {fmtDate(sale.edited_at)} · {sale.edited_by_name}
                </Badge>
              )}
              {sale.id_card_reviewed_at && (
                <Badge className="bg-slate-800 text-slate-400 border-slate-700 text-[10px]">
                  İnceleme: {fmtDate(sale.id_card_reviewed_at)} · {sale.id_card_reviewed_by_name}
                </Badge>
              )}
            </div>
            {sale.id_card_review_note && (
              <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-2 text-xs text-slate-300">
                <span className="text-slate-500">İnceleme notu:</span> {sale.id_card_review_note}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-3">
                <div className="text-[10px] uppercase font-mono text-slate-500 flex items-center gap-1"><Package size={10} /> Ürün</div>
                <div className="font-semibold mt-1">{sale.product_name}</div>
                <div className="text-xs font-mono text-slate-400">{sale.product_sku}</div>
                {sale.category && <Badge className="mt-1 bg-slate-800 text-slate-300 border-slate-700">{sale.category}</Badge>}
              </div>
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
                <div className="text-[10px] uppercase font-mono text-amber-200 flex items-center gap-1"><Zap size={10} /> Puan</div>
                <div className="font-display text-3xl font-bold text-amber-300">+{fmtNum(sale.points)}</div>
                <div className="text-xs text-slate-400">×{sale.quantity} adet</div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-blue-500/20 border border-blue-500/40 flex items-center justify-center">
                    <User size={14} className="text-blue-300" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm">{sale.user_name}</div>
                    <div className="text-[10px] font-mono text-slate-500">{fmtDate(sale.created_at)}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase font-mono text-slate-500">Birim Puan</div>
                  <div className="font-semibold text-blue-300">{fmtNum(sale.unit_points)}</div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-3">
              <div className="text-[10px] uppercase font-mono text-slate-500 mb-1">Müşteri Ad Soyad</div>
              <div className="text-sm font-semibold text-slate-100" data-testid="sale-detail-customer-name">{sale.customer_name || "-"}</div>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-3">
              <div className="text-[10px] uppercase font-mono text-slate-500 mb-1">Müşteri / İşlem Notu</div>
              <div className="text-sm text-slate-200 whitespace-pre-wrap min-h-[2em]">
                {sale.customer_note || <span className="text-slate-500 italic">Not girilmemiş</span>}
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase font-mono text-slate-500 mb-2 flex items-center gap-1">
                <FileImage size={10} /> Kimlik Fotoğrafları
              </div>
              {(!sale.id_card_front_path && !sale.id_card_back_path) ? (
                <div className="text-sm text-slate-500 italic rounded-xl border border-slate-800 bg-slate-900/50 p-4 text-center">
                  Bu satış için kimlik fotoğrafı yüklenmemiş
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-slate-400 mb-1">Ön Yüz</div>
                    {sale.id_card_front_path ? <AuthedImage path={sale.id_card_front_path} downloadName={`kimlik-on-${sale.id?.slice(0,8)}`} />
                      : <div className="aspect-[16/10] rounded-xl bg-slate-800/40 border border-slate-800 flex items-center justify-center text-xs text-slate-600">Yok</div>}
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 mb-1">Arka Yüz</div>
                    {sale.id_card_back_path ? <AuthedImage path={sale.id_card_back_path} downloadName={`kimlik-arka-${sale.id?.slice(0,8)}`} />
                      : <div className="aspect-[16/10] rounded-xl bg-slate-800/40 border border-slate-800 flex items-center justify-center text-xs text-slate-600">Yok</div>}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {sale && !editing && tab === "history" && (
          <div className="space-y-3">
            {!history ? (
              <div className="flex items-center justify-center py-6"><Loader2 className="animate-spin text-slate-500" /></div>
            ) : history.entries.length === 0 ? (
              <div className="text-sm text-slate-500 italic text-center py-6">Henüz işlem kaydı yok</div>
            ) : (
              <div className="relative pl-6 space-y-3" data-testid="sale-history-timeline">
                <div className="absolute left-2 top-2 bottom-2 w-px bg-slate-800" />
                {history.entries.map((e, i) => {
                  const kindMeta = e.kind === "created"
                    ? { label: "Oluşturuldu", cls: "bg-blue-500 text-blue-200" }
                    : e.kind === "id_status"
                    ? { label: "Kimlik Durumu", cls: "bg-amber-500 text-amber-200" }
                    : { label: "Düzenleme", cls: "bg-emerald-500 text-emerald-200" };
                  return (
                    <div key={e.id} className="relative" data-testid={`history-entry-${i}`}>
                      <div className={`absolute -left-5 top-1 h-3 w-3 rounded-full ${kindMeta.cls.split(' ')[0]} ring-4 ring-slate-900`} />
                      <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-3">
                        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <Badge className={`text-[10px] ${kindMeta.cls.replace('bg-', 'bg-')}/20 border-${kindMeta.cls.split('-')[1]}-500/40`}>
                              {kindMeta.label}
                            </Badge>
                            <span className="text-xs text-slate-300 font-medium">{e.by_name || "Sistem"}</span>
                            <span className="text-[10px] text-slate-500 font-mono">{e.by_role}</span>
                          </div>
                          <span className="text-[10px] text-slate-500 font-mono">{fmtDate(e.at)}</span>
                        </div>
                        {e.note && (
                          <div className="text-xs text-slate-400 italic mb-2">"{e.note}"</div>
                        )}
                        {e.changes && Object.keys(e.changes).length > 0 && (
                          <div className="space-y-1">
                            {Object.entries(e.changes).map(([field, diff]) => (
                              <div key={field} className="text-xs flex items-center gap-2 flex-wrap">
                                <span className="text-slate-500 font-mono text-[10px] min-w-[90px]">{field}:</span>
                                <span className="text-slate-400 line-through">{String(diff.from ?? "boş")}</span>
                                <ArrowRight size={10} className="text-slate-600" />
                                <span className="text-emerald-300 font-semibold">{String(diff.to ?? "boş")}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {sale && editing && (
          <div className="space-y-3">
            <div>
              <Label className="text-slate-300">Ürün</Label>
              <Select value={draft.product_id} onValueChange={(v) => setDraft({ ...draft, product_id: v })}>
                <SelectTrigger className="bg-slate-950/50 border-slate-700 mt-1" data-testid="edit-product-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.sku})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <Label className="text-slate-300">Adet</Label>
                <Input type="number" min="1" value={draft.quantity} onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
                       className="bg-slate-950/50 border-slate-700 mt-1" data-testid="edit-quantity" />
              </div>
              <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 px-3 py-2">
                <div className="text-[10px] uppercase font-mono text-amber-200">Yeni Puan</div>
                <div className="font-display text-xl font-bold text-amber-300">{fmtNum(estPoints)}</div>
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Not</Label>
              <Textarea value={draft.customer_note} onChange={(e) => setDraft({ ...draft, customer_note: e.target.value })}
                        className="bg-slate-950/50 border-slate-700 mt-1" rows={2} data-testid="edit-note" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(false)} className="border-slate-700"><X size={14} className="mr-1" /> İptal</Button>
              <Button onClick={saveEdit} className="bg-blue-600 hover:bg-blue-500" data-testid="edit-save-btn"><Save size={14} className="mr-1" /> Kaydet</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* ID Review dialog */}
    <Dialog open={idReviewOpen} onOpenChange={setIdReviewOpen}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck size={18} className="text-emerald-400" /> Kimlik İnceleme</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-slate-300">Durum</Label>
            <Select value={reviewStatus} onValueChange={setReviewStatus}>
              <SelectTrigger className="bg-slate-950/50 border-slate-700 mt-1" data-testid="review-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tamam">Tamam (onaylandı)</SelectItem>
                <SelectItem value="beklemede">Beklemede</SelectItem>
                <SelectItem value="sorunlu">Sorunlu</SelectItem>
                <SelectItem value="eksik">Eksik</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300">Not (opsiyonel)</Label>
            <Textarea value={reviewNote} onChange={(e) => setReviewNote(e.target.value)}
                      className="bg-slate-950/50 border-slate-700 mt-1" rows={2}
                      placeholder="Örn: Arka yüz bulanık, tekrar çekilsin"
                      data-testid="review-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIdReviewOpen(false)} className="border-slate-700">İptal</Button>
          <Button onClick={saveReview} className="bg-emerald-600 hover:bg-emerald-500" data-testid="review-save-btn">Kaydet</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
